import {
  Activity,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Gauge,
  Hash,
  Sigma,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import React from "react";
import { formatTime } from "@globals/format";
import { useMetricMeta } from "@client/hooks/useMetricMeta";
import { useStatsHourly } from "@client/hooks/useStatsHourly";
import { fix, number, unit, unundefined } from "@client/utils/values";

function formatHourLabel(date: Date) {
  const d = new Date(date);
  const hh = d.getHours().toString().padStart(2, "0");
  return `${hh}:00`;
}

type HourCardProps = {
  stats: dailyStats;
  valueF?: (val: string) => string;
};

const HourCard: React.FC<HourCardProps> = React.memo(({ stats, valueF = (val: string) => val }) => {
  const isBooleanStats = stats.uptime !== null || stats.downtime !== null;
  return (
    <div className="p-2 rounded bg-card flex flex-col h-full grow min-w-max shrink-0">
      <h3 className="text-lg font-semibold mb-2">{formatHourLabel(stats.date)}</h3>
      {isBooleanStats ? (
        <div className="space-y-2 text-sm">
          <div className="flex gap-1.5 whitespace-nowrap items-center">
            <ArrowUpDown className="h-4 w-4 text-status-warning" />
            <span className="text-muted-foreground">Počet:</span>
            <span className="font-medium">{stats.risingCount ?? "-"}</span>
          </div>
          <div className="flex gap-1.5 whitespace-nowrap items-center">
            <ArrowUp className="h-4 w-4 text-status-ok" />
            <span className="text-muted-foreground">1:</span>
            <span className="font-medium">
              {stats.uptime != null ? formatTime(Math.round(stats.uptime / 1000) * 1000) : "-"}
            </span>
          </div>
          <div className="flex gap-1.5 whitespace-nowrap items-center">
            <ArrowDown className="h-4 w-4 text-status-serious" />
            <span className="text-muted-foreground">0:</span>
            <span className="font-medium">
              {stats.downtime != null ? formatTime(Math.round(stats.downtime / 1000) * 1000) : "-"}
            </span>
          </div>
        </div>
      ) : (
        <div className="space-y-2 text-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Gauge className="h-4 w-4 text-status-warning" />
              <span className="font-semibold text-2xl leading-none">{valueF(String(stats.avg))}</span>
            </div>
            <div className="flex flex-col items-end gap-1 ml-4">
              <div className="flex items-center gap-1.5">
                <TrendingUp className="h-4 w-4 text-status-serious" />
                <span className="font-medium text-status-serious">{valueF(String(stats.max))}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <TrendingDown className="h-4 w-4 text-status-info" />
                <span className="font-medium text-status-info">{valueF(String(stats.min))}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-1.5">
            <Hash className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">Počet:</span>
            <span className="font-medium">{stats.count}</span>
          </div>
          {stats.median != null && (
            <div className="flex items-center space-x-1.5">
              <Sigma className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Medián:</span>
              <span className="font-medium">{valueF(String(stats.median))}</span>
            </div>
          )}
          {stats.stddev != null && (
            <div className="flex items-center space-x-1.5">
              <Activity className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">σ:</span>
              <span className="font-medium">{valueF(String(stats.stddev))}</span>
            </div>
          )}
          {stats.p05 != null && stats.p95 != null && (
            <div className="flex items-center space-x-1.5">
              <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">p05–p95:</span>
              <span className="font-medium">
                {valueF(String(stats.p05))}–{valueF(String(stats.p95))}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

type HourlyStatsProps = {
  topicId: string;
  boolean?: boolean;
  date?: Date; // optional, defaults to today
};

const HourlyStats: React.FC<HourlyStatsProps> = ({ topicId, boolean = false, date }) => {
  const topic = useMetricMeta(topicId);
  const dateTime = date?.getTime();
  const { stats } = useStatsHourly(topicId, dateTime);

  return (
    <div className="overflow-x-auto flex m-1.5 [scrollbar-width:thin] [scrollbar-color:rgb(163_163_163)_rgb(229_229_229)] dark:[scrollbar-color:rgb(82_82_82)_rgb(38_38_38)]">
      <div className="flex gap-2 m-0.5">
        {stats.map((s, idx) => (
          <HourCard
            key={idx}
            stats={s}
            valueF={
              boolean
                ? (v) => unundefined(v, "---")
                : (v) => unit(unundefined(fix(number(v), topic?.decimals ?? 1), "---"), topic?.unit ?? "")
            }
          />
        ))}
      </div>
    </div>
  );
};

export default React.memo(HourlyStats);
